import React from 'react'
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {formatNumber} from '../../../helpers/UtilityHelper'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE} from '../../../styles/foundation/colors'
import {SHADOW} from '../../../styles/foundation/effects'
import {helpTextSx, helpTextTypeSx} from '../../../styles/patterns'
import {getPerformanceColor, PERF_COLORS} from './performanceUtils'

const statCardSx = {
  bgcolor: WHITE, borderRadius: '10px', p: '16px', textAlign: 'center',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover},
}
const headerCellSx = {
  fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE,
  textTransform: 'uppercase', letterSpacing: '0.03em', py: 1.5, whiteSpace: 'nowrap',
}
const dataCellSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 1.2}

function PerformanceCell({value, type = 'default'}) {
  const color = getPerformanceColor(value, type)
  return (
    <Typography sx={{
      fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color,
      bgcolor: color + '14', px: 1, py: 0.25, borderRadius: '6px', display: 'inline-block',
    }}>
      {value != null ? `${value}%` : '—'}
    </Typography>
  )
}

export default function ReversePerformance({data = []}) {
  if (data.length === 0) return null

  const totalVol = data.reduce((s, d) => s + d.volume, 0)
  const avgPickup = totalVol > 0
    ? (data.reduce((s, d) => s + d.rvpPickupPct * d.volume, 0) / totalVol).toFixed(1)
    : 0
  const best = data.reduce((a, b) => a.rvpPickupPct > b.rvpPickupPct ? a : b)

  return (
    <>
      <Box sx={{display: 'flex', gap: '12px', mb: '20px', flexWrap: 'wrap'}}>
        {[
          {label: 'Total RVP Requests', value: formatNumber(totalVol), color: NAVY},
          {label: 'Avg Pickup %', value: `${avgPickup}%`, color: getPerformanceColor(parseFloat(avgPickup))},
          {label: 'Best Performer', value: best.courierName, color: PERF_COLORS.green, sub: `${best.rvpPickupPct}%`},
        ].map((s) => (
          <Box key={s.label} sx={{...statCardSx, flex: '1 1 180px', minWidth: 160}}>
            <Typography sx={{fontSize: s.sub ? SIZE.sm : '22px', fontWeight: WEIGHT.extra, lineHeight: 1.2, color: s.color}}>
              {s.value}
            </Typography>
            {s.sub && <Typography sx={{fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: s.color, mt: 0.3}}>{s.sub}</Typography>}
            <Typography sx={{color: SUBTLE, fontSize: SIZE['2xs'], mt: '2px', fontWeight: WEIGHT.medium, fontFamily: FONT}}>{s.label}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={helpTextSx}>
        <InfoOutlinedIcon sx={{fontSize: 16, color: '#3783e7', mt: 0.2}} />
        <Typography sx={helpTextTypeSx}>
          <strong>RVP Metrics:</strong> Pickup % = picked up within TAT. +1/+2 = cumulative adherence including buffer days.
        </Typography>
      </Box>

      <Box sx={{bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, overflow: 'hidden'}}>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{bgcolor: '#f8f9fc'}}>
                <TableCell sx={headerCellSx}>#</TableCell>
                <TableCell sx={headerCellSx}>Courier</TableCell>
                <TableCell sx={headerCellSx} align='right'>RVP Requests</TableCell>
                <TableCell sx={headerCellSx} align='right'>Picked Up</TableCell>
                <TableCell sx={headerCellSx} align='right'>Pickup %</TableCell>
                <TableCell sx={headerCellSx} align='right'>Pickup +1 %</TableCell>
                <TableCell sx={headerCellSx} align='right'>Pickup +2 %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, i) => (
                <TableRow key={row.courierName} sx={{'&:hover': {bgcolor: '#fafbfc'}}}>
                  <TableCell sx={dataCellSx}>
                    <Box sx={{width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: WEIGHT.bold, fontFamily: FONT, bgcolor: '#f3f4f6', color: SUBTLE}}>
                      {i + 1}
                    </Box>
                  </TableCell>
                  <TableCell sx={{...dataCellSx, fontWeight: WEIGHT.bold, color: NAVY}}>{row.courierName}</TableCell>
                  <TableCell sx={dataCellSx} align='right'>{formatNumber(row.volume)}</TableCell>
                  <TableCell sx={dataCellSx} align='right'>{formatNumber(row.totalPickedUp)}</TableCell>
                  <TableCell align='right'><PerformanceCell value={row.rvpPickupPct} /></TableCell>
                  <TableCell align='right'><PerformanceCell value={row.rvpPlusOnePct} /></TableCell>
                  <TableCell align='right'><PerformanceCell value={row.rvpPlusTwoPct} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  )
}
